import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Play, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/brand";

const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    const handlePlay = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = false;
        videoRef.current.play();
        setPlaying(true);
    };

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
        >
            {/* Soft warm background block */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 right-0 w-[60%] h-[70%] bg-beige-100 rounded-bl-[120px]" />
                <div className="absolute inset-0 bg-grain opacity-40" />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Text */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-stone-200 mb-7">
                        <Sparkles size={14} className="text-gold" />
                        <span className="text-xs tracking-[0.2em] uppercase text-stone-700">
                            {t("hero.badge")}
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif text-5xl sm:text-6xl lg:text-7xl text-stone-900 leading-[1.02] tracking-tight"
                    >
                        {t("hero.title_1")}{" "}
                        <em className="italic text-gold-deep font-normal">
                            {t("hero.title_em")}
                        </em>{" "}
                        {t("hero.title_2")}
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="mt-7 text-lg md:text-xl text-stone-600 max-w-xl font-light leading-relaxed"
                    >
                        {t("hero.subtitle")}
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Button
                            onClick={() => scrollTo("#quiz")}
                            className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-8 py-6 text-base h-auto"
                            data-testid="hero-primary-cta"
                        >
                            {t("hero.cta_primary")}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => scrollTo("#como-funciona")}
                            className="rounded-full bg-transparent border-2 border-stone-200 hover:border-stone-900 text-stone-900 px-8 py-6 text-base h-auto"
                            data-testid="hero-secondary-cta"
                        >
                            {t("hero.cta_secondary")}
                        </Button>
                    </div>

                    <div className="mt-12 flex items-center gap-6 text-stone-500 text-sm">
                        <div className="flex -space-x-2">
                            {[BRAND.images.family, BRAND.images.couple, BRAND.images.gourmet].map((src) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt=""
                                    className="h-9 w-9 rounded-full object-cover ring-2 ring-white"
                                />
                            ))}
                        </div>
                        <span>{t("hero.social_proof")}</span>
                    </div>
                </motion.div>

                {/* Video */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6 relative"
                >
                    <div className="relative aspect-[4/5] sm:aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-stone-200 bg-stone-900">
                        <video
                            ref={videoRef}
                            data-testid="hero-video"
                            className="absolute inset-0 w-full h-full object-cover"
                            src={BRAND.video}
                            poster={BRAND.images.hero}
                            playsInline
                            loop
                            muted
                            autoPlay
                        />
                        {!playing && (
                            <button
                                onClick={handlePlay}
                                aria-label={t("hero.play_aria")}
                                data-testid="hero-video-play"
                                className="absolute inset-0 flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors group"
                            >
                                <span className="w-20 h-20 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform">
                                    <Play
                                        size={28}
                                        className="text-stone-900 ml-1"
                                        fill="currentColor"
                                    />
                                </span>
                            </button>
                        )}
                    </div>

                    {/* Floating badge */}
                    <div className="hidden md:block absolute -left-6 -bottom-6 bg-white rounded-2xl shadow-xl px-5 py-4 ring-1 ring-stone-200 max-w-[220px]">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="gold-divider" />
                            <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">
                                {t("hero.badge_premium")}
                            </span>
                        </div>
                        <p className="font-serif text-lg text-stone-900 leading-tight">
                            {t("hero.badge_text")}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
